import { useState, useCallback, useRef, useEffect } from 'react';
import {
  uploadPresign,
  uploadComplete,
  uploadStatus,
  uploadTouch,
  getPresignedUrl,
  graphqlRequest,
  listRecentUploads,
  validateUploadFile,
  mapPresignError,
  isDuplicateUploadError,
} from '../common/apiClient';
import { computeContentMd5 } from '../utils/contentHash';
import { isTerminalQueueStatus, mapDocumentToUploadStatus } from '../utils/uploadStatusMapper';
import { loadUploadQueue, saveUploadQueue, clearUploadQueue, todayKeyART } from '../utils/uploadQueueStorage';
import { mergeUploadQueues } from '../utils/uploadQueueMerge';
import { getTenantIdFromSession } from '../common/auth';

const USE_UPLOAD_V1 = process.env.REACT_APP_UPLOAD_V1_ENABLED === 'true';

const TERMINAL_STATUSES = new Set([
  'done',
  'error',
  'duplicate',
  'needs_review',
  'rejected',
  'upload_error',
]);

const RESUMABLE_STATUSES = new Set(['queued', 'processing', 'reprocessing', 'presigning', 'uploading', 'validating']);

const DOCUMENT_STATUS_QUERY = `
  query UploadDocumentStatus($id: String!) {
    getDocumentById(id: $id) {
      document_id
      status
      doc_type
      error
      redirect_target_document_id
      redirect_target_doc_type
      arca_validation {
        status
        severity
        checked_at
        observations
        errors
      }
      bedrock_model_output {
        tipo_documento
        letra_documento
        codigo_factura
        categoria_documento
        title
      }
    }
  }
`;

function putWithProgress(url, file, headers, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    if (headers) {
      Object.entries(headers).forEach(([name, value]) => {
        xhr.setRequestHeader(name, value);
      });
    }
    xhr.upload.onprogress = (event) => {
      if (onProgress && event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (onProgress) onProgress(100);
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

async function requestPresign(file, contentMd5) {
  if (USE_UPLOAD_V1) {
    try {
      const pres = await uploadPresign({
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
        content_md5: contentMd5,
      });
      return { mode: 'v1', pres };
    } catch (err) {
      if (isDuplicateUploadError(err) || err?.status === 409) throw err;
      if (err?.status === 415 || err?.status === 413 || err?.status === 429) throw err;
    }
  }

  const pres = await getPresignedUrl({
    key: 'documents/unclassified',
    contentType: file.type,
    presigned_type: 'put_object',
    file_name: file.name,
    content_md5: contentMd5,
    file_size: file.size,
  });
  return { mode: 'legacy', pres };
}

export function useUpload() {
  const [tenantId, setTenantId] = useState('');
  const [items, setItems] = useState([]);
  const [queueDate, setQueueDate] = useState(todayKeyART());
  const pollTimers = useRef({});
  const pollTargetRef = useRef({});
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    let cancelled = false;
    getTenantIdFromSession()
      .then((tid) => {
        if (cancelled) return;
        const resolved = tid && tid !== 'pending' ? tid : '';
        setTenantId(resolved);
        if (resolved) {
          setItems(loadUploadQueue(resolved));
        }
      })
      .catch(() => {
        if (!cancelled) setTenantId('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hydrateFromServer = useCallback(async () => {
    if (!tenantId) return;
    try {
      const result = await listRecentUploads(5);
      const serverDate = result.queueDate || todayKeyART();
      setQueueDate(serverDate);
      const serverItems = result.items || [];
      const local = loadUploadQueue(tenantId);
      if (serverDate !== todayKeyART() && local.length) {
        setItems(mergeUploadQueues(serverItems, []));
        return;
      }
      if (serverItems.length === 0) {
        const resumableLocal = local.filter(
          (item) => item.file && RESUMABLE_STATUSES.has(item.status),
        );
        setItems(resumableLocal);
        if (local.length && !resumableLocal.length) {
          clearUploadQueue();
        }
        return;
      }
      setItems((prev) => mergeUploadQueues(serverItems, prev.length ? prev : local));
    } catch {
      /* offline — keep local cache */
    }
  }, [tenantId]);

  const stopPoll = useCallback((id) => {
    if (pollTimers.current[id]) {
      clearInterval(pollTimers.current[id]);
      delete pollTimers.current[id];
    }
  }, []);

  const applyPatch = useCallback((id, changes) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)));
  }, []);

  const startPollLegacy = useCallback((id, documentId) => {
    if (!documentId) return;
    stopPoll(id);
    pollTargetRef.current[id] = documentId;

    const tick = async () => {
      try {
        const targetId = pollTargetRef.current[id];
        if (!targetId) return;
        const data = await graphqlRequest(DOCUMENT_STATUS_QUERY, { id: targetId });
        const mapped = mapDocumentToUploadStatus(data.getDocumentById);
        if (mapped.doc_id && mapped.doc_id !== pollTargetRef.current[id]) {
          pollTargetRef.current[id] = mapped.doc_id;
        }
        applyPatch(id, mapped);
        if (isTerminalQueueStatus(mapped.status)) {
          stopPoll(id);
        }
      } catch {
        /* keep polling */
      }
    };

    tick();
    pollTimers.current[id] = setInterval(tick, 2000);
  }, [applyPatch, stopPoll]);

  const startPollV1 = useCallback((id, uploadId) => {
    stopPoll(id);
    pollTimers.current[id] = setInterval(async () => {
      try {
        const status = await uploadStatus(uploadId);
        applyPatch(id, {
          status: status.status,
          doc_id: status.doc_id,
          doc_type: status.doc_type,
          duplicate_of: status.duplicate_of,
          error: status.error_message,
        });
        if (TERMINAL_STATUSES.has(status.status)) {
          stopPoll(id);
        }
      } catch {
        stopPoll(id);
      }
    }, 1800);
  }, [applyPatch, stopPoll]);

  const patch = useCallback((id, changes) => {
    applyPatch(id, changes);
    if ((changes.status === 'processing' || changes.status === 'reprocessing') && changes.doc_id) {
      startPollLegacy(id, changes.doc_id);
    }
  }, [applyPatch, startPollLegacy]);

  useEffect(() => {
    if (tenantId) saveUploadQueue(items, queueDate, tenantId);
  }, [items, queueDate, tenantId]);

  useEffect(() => {
    if (!tenantId) return undefined;
    hydrateFromServer();

    const onFocus = () => {
      hydrateFromServer();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [hydrateFromServer, tenantId]);

  useEffect(() => {
    items.forEach((item) => {
      if (!RESUMABLE_STATUSES.has(item.status) || pollTimers.current[item.id]) return;
      if (item.upload_id && USE_UPLOAD_V1) {
        startPollV1(item.id, item.upload_id);
      } else if (item.doc_id) {
        startPollLegacy(item.id, item.doc_id);
      }
    });
  }, [items, startPollLegacy, startPollV1]);

  useEffect(() => () => {
    Object.keys(pollTimers.current).forEach(stopPoll);
  }, [stopPoll]);

  const runUpload = useCallback(async (id, file) => {
    let phase = 'presigning';
    try {
      patch(id, { status: 'presigning', pct: 0, error: null });
      const contentMd5 = await computeContentMd5(file);
      const { mode, pres } = await requestPresign(file, contentMd5);

      phase = 'uploading';
      patch(id, {
        status: 'uploading',
        upload_id: pres.upload_id || null,
        doc_id: pres.document_id,
        pct: 0,
      });
      await putWithProgress(pres.url, file, pres.headers, (pct) => patch(id, { pct }));

      if (mode === 'v1') {
        await uploadComplete(pres.upload_id);
        patch(id, { status: 'queued', pct: 100 });
        startPollV1(id, pres.upload_id);
      } else {
        if (pres.upload_id) {
          await uploadTouch(pres.upload_id).catch(() => {});
        }
        patch(id, { status: 'queued', pct: 100 });
        startPollLegacy(id, pres.document_id);
      }
    } catch (err) {
      if (isDuplicateUploadError(err) || err?.status === 409) {
        const data = err.data || {};
        patch(id, {
          status: 'duplicate',
          error: mapPresignError(409, data) || err.message,
          duplicate_of: data.duplicate_of || data.existing_document_id || err.existingDocumentId,
          doc_id: data.existing_document_id || err.existingDocumentId,
        });
        return;
      }
      if (err?.status === 415 || err?.status === 413 || err?.status === 429) {
        patch(id, {
          status: 'rejected',
          error: mapPresignError(err.status, err.data),
        });
        return;
      }
      patch(id, {
        status: phase === 'uploading' ? 'upload_error' : 'error',
        error: err.message || 'Error de subida',
      });
    }
  }, [patch, startPollV1, startPollLegacy]);

  const addFiles = useCallback((fileList) => {
    Array.from(fileList).forEach((file) => {
      const id = crypto.randomUUID();
      const createdAt = new Date().toISOString();
      const rejection = validateUploadFile(file);
      const item = {
        id,
        file,
        name: file.name,
        size: file.size,
        status: rejection ? 'rejected' : 'validating',
        pct: 0,
        error: rejection,
        upload_id: null,
        doc_id: null,
        doc_type: null,
        duplicate_of: null,
        comprobante_label: null,
        comprobante_subtype: null,
        createdAt,
        updatedAt: createdAt,
      };
      setItems((prev) => [item, ...prev].slice(0, 5));
      if (!rejection) {
        setTimeout(() => runUpload(id, file), 0);
      }
    });
  }, [runUpload]);

  const retry = useCallback((id) => {
    const it = itemsRef.current.find((x) => x.id === id);
    if (!it?.file) return;
    stopPoll(id);
    patch(id, { status: 'validating', error: null, pct: 0 });
    runUpload(id, it.file);
  }, [patch, runUpload, stopPoll]);

  const remove = useCallback((id) => {
    stopPoll(id);
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, [stopPoll]);

  return { items, addFiles, retry, remove, patchItem: patch };
}
